import { useState, useRef, useEffect } from 'react';
import { Upload, AlertCircle, AlertTriangle, Trash2, Play } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldIssue } from '../../types';
import type { ValidationFeedback } from '@/api/campaigns';

// ─── Shared sub-components used across all ad-details format components ────────

// ─── Upload ratios ────────────────────────────────────────────────────────────
// The shape of an individual uploaded asset, as stored in
// `campaign_creative_assets.ratio`. Distinct from the `Ratio` type in ../../utils,
// which selects the mockup/tab variant ('reel' is a placement, not a shape).
//
// 4:5 is a first-class value here. Previously 4:5 assets were uploaded through
// the 1:1 slot and persisted as '1x1', losing the true shape of the file.
export type UploadRatio = '1x1' | '4x5' | '9x16';

// Exhaustive map rather than a ternary: adding a member to UploadRatio must be a
// compile error here, not a silent fallthrough to the wrong aspect class.
const ASPECT_CLASS: Record<UploadRatio, string> = {
  '1x1': 'aspect-square',
  '4x5': 'aspect-[4/5]',
  '9x16': 'aspect-[9/16]',
};

export function SubSectionLabel({ label }: { label: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
      {label}
    </p>
  );
}

export function CharCount({ value, limit }: { value: string; limit: number }) {
  const over = value.length > limit;
  return (
    <span
      className={`text-xs tabular-nums ${over ? 'text-amber-600 font-medium' : 'text-slate-400'}`}
    >
      {value.length}/{limit}
    </span>
  );
}

export function FieldLabel({
  label,
  right,
  tooltip,
}: {
  label: React.ReactNode;
  right?: React.ReactNode;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 cursor-default">
              {label}
            </p>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-64">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      )}
      {right}
    </div>
  );
}

export function InlineIssue({
  issue,
  showErrors,
  showWarnings,
  inline = false,
}: {
  issue?: FieldIssue;
  showErrors: boolean;
  showWarnings: boolean;
  inline?: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
}) {
  if (!issue) return null;
  const errorMsgs = showErrors
    ? issue.errors.map((m) => ({ type: 'error' as const, text: m }))
    : [];
  const warningMsgs = showWarnings
    ? issue.warnings.map((m) => ({ type: 'warning' as const, text: m }))
    : [];
  const msgs = [...errorMsgs, ...warningMsgs];
  if (msgs.length === 0) return null;
  return (
    <div className={`space-y-0.5 ${inline ? '' : 'mt-1.5'}`}>
      {msgs.map((m, i) => (
        <div key={i} className="flex items-start gap-1.5">
          <p
            className={`text-xs flex items-start gap-1.5 flex-1 ${m.type === 'error' ? 'text-red-600' : 'text-amber-600'}`}
          >
            {m.type === 'error' ? (
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            )}
            {m.text}
          </p>
        </div>
      ))}
    </div>
  );
}

// A complete field: label + control (children) + inline issue, keyed by
// fieldIssues[fieldKey]. The InlineIssue is ALWAYS rendered, so it can never be
// forgotten when adding a new field (the cause of the legal-field inline bug).
export function Field({
  label,
  tooltip,
  right,
  help,
  fieldKey,
  fieldIssues,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
  inline = false,
  children,
}: {
  label?: React.ReactNode;
  tooltip?: string;
  right?: React.ReactNode; // e.g. <CharCount/>
  help?: React.ReactNode; // e.g. legal "Enter N/A..." helper text
  fieldKey: string; // key into fieldIssues, e.g. 'legal'
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  inline?: boolean;
  children: React.ReactNode; // the <input>/<textarea> control
}) {
  const issue = fieldIssues[fieldKey];
  // Hide the static helper text when a visible issue is shown, so we don't
  // duplicate guidance (e.g. the legal "enter N/A" note repeating the error).
  const hasVisibleIssue =
    !!issue &&
    ((showErrors && issue.errors.length > 0) || (showWarnings && issue.warnings.length > 0));
  return (
    <div>
      {label && <FieldLabel label={label} tooltip={tooltip} right={right} />}
      {children}
      <InlineIssue
        issue={issue}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
        inline={inline}
      />
      {help && !hasVisibleIssue && <p className="mt-1 text-xs text-slate-400">{help}</p>}
    </div>
  );
}

export const inputClass =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50 transition-all';

export function issueBorder(
  issue: FieldIssue | undefined,
  showErrors: boolean,
  showWarnings: boolean,
): string {
  if (!issue) return '';
  if (issue.errors.length > 0 && showErrors)
    return 'border-red-400 focus:border-red-400 focus:ring-red-100';
  if (issue.warnings.length > 0 && showWarnings)
    return 'border-amber-400 focus:border-amber-400 focus:ring-amber-100';
  return '';
}

export function UploadZone({
  onFile,
  hint,
  accept = 'image/*,video/*',
  issueType,
}: {
  onFile: (file: File) => void;
  hint: string;
  accept?: string;
  issueType?: 'error' | 'warning';
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFile(dropped);
  }

  const borderClass = isDragging
    ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
    : issueType === 'error'
      ? 'border-red-400 bg-red-50/20 hover:border-red-500'
      : issueType === 'warning'
        ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
        : 'border-dashed border-slate-200 hover:border-slate-300 hover:bg-slate-50';

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`h-36 w-full rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 px-4 ${borderClass}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
          isDragging ? 'bg-[#0014cc]/10' : 'bg-slate-100'
        }`}
      >
        <Upload
          className={`w-4 h-4 transition-colors ${isDragging ? 'text-[#0014cc]' : 'text-slate-400'}`}
        />
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500 font-medium">{isDragging ? 'Drop to upload' : hint}</p>
        {!isDragging && <p className="text-[11px] text-slate-400 mt-0.5">or drag & drop</p>}
      </div>
    </button>
  );
}

// Shared inner layout for both AssetPreview and AssetPreviewFromUrl
function AssetPreviewShell({
  isVideo,
  src,
  onRemove,
  ratio,
  issueType,
  isSelected,
}: {
  isVideo: boolean;
  src: string;
  onRemove: () => void;
  ratio: UploadRatio;
  issueType?: 'error' | 'warning';
  /** Draws the "showing in the mockup" ring. Undefined for slots that aren't selectable. */
  isSelected?: boolean;
}) {
  const aspectClass = ASPECT_CLASS[ratio];
  const mediaClass = `h-full w-full object-cover rounded-lg ${
    issueType === 'error'
      ? 'border-2 border-red-400'
      : issueType === 'warning'
        ? 'border-2 border-amber-400'
        : 'border border-slate-200'
  }`;

  return (
    <div className="h-36 flex items-center justify-center">
      {/* The ring goes on this box — the one that carries the aspect ratio — so it
          hugs the media exactly, the way the Dynamic Creative thumbnails do. Putting
          it on an outer wrapper leaves a gap, because this box is only as wide as its
          aspect ratio allows while the wrapper spans the full column. */}
      <div
        className={`relative h-full ${aspectClass} group/img rounded-lg ${
          isSelected ? 'ring-2 ring-[#0014cc]' : ''
        }`}
      >
        {isVideo ? (
          // Render the video element directly in the DOM — the browser handles decoding
          // and displays the frame natively once it has loaded enough data.
          // preload="auto" fetches real frame data; seeking to 0.1s on loadeddata avoids
          // codec-dependent blank first frames. Works for both blob URLs and GCS URLs.
          <video
            src={src}
            muted
            playsInline
            preload="auto"
            className={mediaClass}
            onLoadedData={(e) => {
              e.currentTarget.currentTime = 0.1;
            }}
          />
        ) : (
          <img src={src} alt="asset" className={mediaClass} />
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover/img:opacity-0 transition-opacity">
            <div className="w-9 h-9 rounded-full bg-black/50 flex items-center justify-center">
              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Collapse the issues for one or more field keys into a single issue.
//
// A slot usually watches one key, but the two feed slots also share a key:
// "1:1 or 4:5 is missing" is one problem about the pair, not about either slot,
// so it has to render on both. Assigning it to just one slot put the red border
// on the 1:1 and left the 4:5 looking fine.
export function mergeIssues(
  fieldKey: string | string[],
  fieldIssues: Record<string, FieldIssue>,
): FieldIssue | undefined {
  if (!Array.isArray(fieldKey)) return fieldIssues[fieldKey];
  const present = fieldKey.map((k) => fieldIssues[k]).filter(Boolean);
  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0];
  return {
    errors: present.flatMap((i) => i.errors),
    warnings: present.flatMap((i) => i.warnings),
    warningMeta: present.flatMap((i) => i.warningMeta ?? []),
  };
}

// A complete single-asset upload slot: label, then whichever of the three
// mutually exclusive states applies (pending file / saved asset / empty zone),
// then the inline issue.
//
// Extracted because the 1:1, 4:5 and 9:16 columns were the same 45 lines three
// times over, and the copies had already begun to drift (the 9:16 column fell
// back to the 1:1 content type; the others did not). One definition means a fix
// lands in every slot at once.
//
// `isActive`/`onSelect` are optional and only meaningful for slots that share a
// mockup: 1:1 and 4:5 are one feed format, so only one can be previewed at a
// time and clicking a slot chooses which. Slots with their own mockup (9:16)
// simply omit them and render no click affordance.
export function AssetSlot({
  label,
  tooltip,
  hint,
  ratio,
  fieldKey,
  file,
  onFile,
  existingUrl,
  existingContentType,
  onClearExisting,
  fieldIssues,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
  isActive,
  onSelect,
}: {
  label: string;
  tooltip: string;
  hint: string;
  ratio: UploadRatio;
  /** One or more keys into fieldIssues. Multiple keys are merged, which is how a
      shared issue like "ratio_items.feed" (meaning "1:1 or 4:5 is missing") can
      light up both feed slots instead of being pinned to one of them. */
  fieldKey: string | string[];
  file: File | null;
  onFile: (f: File | null) => void;
  existingUrl?: string;
  existingContentType?: string | null;
  onClearExisting: () => void;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  isActive?: boolean;
  onSelect?: () => void;
}) {
  const issue = mergeIssues(fieldKey, fieldIssues);
  // Severity shown on the media border, respecting the header toggles.
  const issueType: 'error' | 'warning' | undefined = !issue
    ? undefined
    : issue.errors.length > 0 && showErrors
      ? 'error'
      : issue.warnings.length > 0 && showWarnings
        ? 'warning'
        : undefined;

  const hasAsset = !!file || !!existingUrl;
  const selectable = !!onSelect && hasAsset;
  // Only draw the ring on a slot that can actually be chosen — a lone filled slot
  // is already what the mockup shows, so a ring there would be noise.
  const showRing = selectable && !!isActive;

  const preview = file ? (
    <AssetPreview
      file={file}
      onRemove={() => onFile(null)}
      ratio={ratio}
      issueType={issueType}
      isSelected={showRing}
    />
  ) : existingUrl ? (
    <AssetPreviewFromUrl
      url={existingUrl}
      contentType={existingContentType}
      onRemove={onClearExisting}
      ratio={ratio}
      issueType={issueType}
      isSelected={showRing}
    />
  ) : (
    <UploadZone onFile={onFile} hint={hint} issueType={issueType} />
  );

  return (
    <div>
      <FieldLabel label={label} tooltip={tooltip} />
      <div className="border-b border-slate-200 mb-3" />
      {selectable ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Not a <button>: the preview already contains a delete <button>, and
                nesting interactive elements is invalid HTML and breaks keyboard nav.
                No ring/opacity here — the ring is drawn on the media box inside, so
                it hugs the asset instead of this full-column wrapper. */}
            <div
              // Not role="radio": that promises a surrounding radiogroup with
              // arrow-key navigation, which this isn't. It's a "show this one in
              // the mockup" toggle, so button + aria-pressed is the honest fit.
              role="button"
              aria-pressed={!!isActive}
              aria-label={`Preview ${label} in the mockup`}
              tabIndex={0}
              onClick={onSelect}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect?.();
                }
              }}
              className="cursor-pointer"
            >
              {preview}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isActive ? 'Currently showing in mockup' : 'Click to preview in mockup'}
          </TooltipContent>
        </Tooltip>
      ) : (
        preview
      )}
      <InlineIssue
        issue={issue}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
      />
    </div>
  );
}

// Asset preview from an existing URL (e.g. signed GCS URL returned by the API)
export function AssetPreviewFromUrl({
  url,
  contentType,
  onRemove,
  ratio,
  issueType,
  isSelected,
}: {
  url: string;
  contentType?: string | null;
  onRemove: () => void;
  ratio: UploadRatio;
  issueType?: 'error' | 'warning';
  isSelected?: boolean;
}) {
  const isVideo = !!contentType?.startsWith('video/');

  return (
    <AssetPreviewShell
      isVideo={isVideo}
      src={url}
      onRemove={onRemove}
      ratio={ratio}
      issueType={issueType}
      isSelected={isSelected}
    />
  );
}

// A single uploaded asset preview (1:1, 4:5 or 9:16) with a trash button on hover
export function AssetPreview({
  file,
  onRemove,
  ratio,
  issueType,
  isSelected,
}: {
  file: File;
  onRemove: () => void;
  ratio: UploadRatio;
  issueType?: 'error' | 'warning';
  isSelected?: boolean;
}) {
  const isVideo = file.type.startsWith('video/');
  // Use state+effect (not useMemo+effect) so the URL is created and revoked
  // within the same effect — avoids React 18 strict-mode revoking the URL
  // on the simulated unmount while useMemo returns the stale revoked value.
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!src) return null;

  return (
    <AssetPreviewShell
      isVideo={isVideo}
      src={src}
      onRemove={onRemove}
      ratio={ratio}
      issueType={issueType}
      isSelected={isSelected}
    />
  );
}
